// AI transfer system — swaps players between CPU clubs every season.
// Keeps the league fresh without affecting the user's save too aggressively.
//
// How it works:
// 1. For each non-user CPU team, pick 1-3 "rotation candidates" (lowest OVR outfield players not starters)
// 2. Swap them with rotation candidates from another CPU team of similar reputation
// 3. Occasionally generate a brand-new regen to replace an aging player (age >= 33)
// 4. Returns { updatedTeams, transferLog: [{from, to, playerName}] }

const pickRotationCandidates = (team, count = 2) => {
  if (!team.players || team.players.length < 18) return [];
  // Sort by OVR asc, exclude GK 1st choice
  const nonStarters = [...team.players].sort((a, b) => (a.overall || 60) - (b.overall || 60)).slice(0, 8);
  return nonStarters.slice(0, count);
};

const genRegenPlayer = (teamId) => {
  const firstNames = ['Takumi', 'Shota', 'Ren', 'Haruto', 'Sora', 'Yuki', 'Kaito', 'Daichi', 'Ryu', 'Hiro'];
  const lastNames = ['Tanaka', 'Suzuki', 'Sato', 'Ito', 'Yamamoto', 'Kato', 'Yoshida', 'Yamada', 'Sasaki', 'Watanabe'];
  const positions = ['GK','CB','LB','RB','CDM','CM','CAM','LW','RW','ST'];
  const position = positions[Math.floor(Math.random() * positions.length)];
  const age = 18 + Math.floor(Math.random() * 3);
  const overall = 60 + Math.floor(Math.random() * 12);
  return {
    id: `regen_${teamId}_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    name: `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`,
    position,
    age,
    overall,
    potential: Math.min(95, overall + 10 + Math.floor(Math.random() * 15)),
    nationality: 'Japan',
    flag: '🇯🇵',
    number: 1 + Math.floor(Math.random() * 40),
    injuredWeeks: 0,
    stats: {},
  };
};

// Swap one player between two teams
const swapPlayers = (teamA, teamB, playerAId, playerBId) => {
  const pa = teamA.players.find(p => p.id === playerAId);
  const pb = teamB.players.find(p => p.id === playerBId);
  if (!pa || !pb) return { teamA, teamB };
  return {
    teamA: { ...teamA, players: teamA.players.map(p => p.id === playerAId ? { ...pb } : p) },
    teamB: { ...teamB, players: teamB.players.map(p => p.id === playerBId ? { ...pa } : p) },
  };
};

// Main: run AI transfer window across all teams (excluding user's team in RTG)
export const runAITransferWindow = (teams, { excludeTeamId = null, intensity = 0.6 } = {}) => {
  let workingTeams = teams.map(t => ({ ...t, players: [...(t.players || [])] }));
  const log = [];
  const cpuTeams = workingTeams.filter(t => t.id !== excludeTeamId);
  // Step 1: retire age 35+, replace with regen
  cpuTeams.forEach(t => {
    const aged = t.players.filter(p => (p.age || 0) >= 35);
    aged.forEach(old => {
      const regen = genRegenPlayer(t.id);
      t.players = t.players.map(p => p.id === old.id ? regen : p);
      log.push({ type: 'retire', from: t.name, player: old.name, replacement: regen.name });
    });
  });
  // Step 2: swap rotation players across teams
  const swapCount = Math.floor(cpuTeams.length * intensity); // e.g. 0.6 * 20 = 12 swaps
  for (let i = 0; i < swapCount; i++) {
    const teamA = cpuTeams[Math.floor(Math.random() * cpuTeams.length)];
    const teamB = cpuTeams[Math.floor(Math.random() * cpuTeams.length)];
    if (!teamA || !teamB || teamA.id === teamB.id) continue;
    const candA = pickRotationCandidates(teamA, 1)[0];
    const candB = pickRotationCandidates(teamB, 1)[0];
    if (!candA || !candB) continue;
    const result = swapPlayers(teamA, teamB, candA.id, candB.id);
    // reinsert into workingTeams
    workingTeams = workingTeams.map(t => t.id === teamA.id ? result.teamA : t.id === teamB.id ? result.teamB : t);
    log.push({ type: 'swap', from: teamA.name, to: teamB.name, outPlayer: candA.name, inPlayer: candB.name });
  }
  // Step 3: occasional star-player buy from CPU to CPU (boosted rating swap)
  const bigMoves = Math.max(1, Math.floor(cpuTeams.length * 0.1));
  for (let i = 0; i < bigMoves; i++) {
    const buyer = cpuTeams[Math.floor(Math.random() * cpuTeams.length)];
    const seller = cpuTeams[Math.floor(Math.random() * cpuTeams.length)];
    if (!buyer || !seller || buyer.id === seller.id) continue;
    // pick seller's 3rd-best player
    const star = [...seller.players].sort((a, b) => (b.overall || 0) - (a.overall || 0))[2];
    const weak = [...buyer.players].sort((a, b) => (a.overall || 0) - (b.overall || 0))[0];
    if (!star || !weak) continue;
    const result = swapPlayers(buyer, seller, weak.id, star.id);
    workingTeams = workingTeams.map(t => t.id === buyer.id ? result.teamA : t.id === seller.id ? result.teamB : t);
    log.push({ type: 'big-move', from: seller.name, to: buyer.name, outPlayer: weak.name, inPlayer: star.name });
  }
  return { teams: workingTeams, log };
};
